import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Payment,
  AccountBalanceWallet,
  PhoneAndroid,
  AttachMoney,
  Download,
  CheckCircle,
  AccountBalance
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface InstitutionInfo {
  name: string;
  nit: string;
  account: string;
  bank: string;
}

interface PaymentOption {
  icon: React.ReactElement;
  title: string;
  description: string | React.ReactNode;
  details: string[];
  highlight?: boolean;
  badge?: string;
}

const PaymentInstructions: React.FC = () => {
  const theme = useTheme();

  const institutionInfo: InstitutionInfo = {
    name: "Institución Educativa José Eusebio Caro",
    nit: "800046335",
    account: "370-149678963-01",
    bank: "Banco Mundo Mujer"
  };

  const paymentOptions: PaymentOption[] = [
    {
      icon: <Payment sx={{ color: theme.palette.success.main }} />,
      title: "Efecty",
      description: "Pago en convenio con Banco Mundo Mujer",
      details: [
        `NIT: ${institutionInfo.nit}`,
        `Cuenta: ${institutionInfo.account}`
      ]
    },
    {
      icon: <AccountBalanceWallet sx={{ color: theme.palette.secondary.main }} />,
      title: "Transferencias Interbancarias",
      description: (
        <>
          Desde otros bancos <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>(No Bre-B)</Box>
        </>
      ),
      details: [
        `Banco destino: ${institutionInfo.bank}`,
        `NIT: ${institutionInfo.nit}`,
        `Cuenta de Ahorros: ${institutionInfo.account}`
      ]
    },
    {
      icon: <PhoneAndroid sx={{ color: theme.palette.primary.dark }} />,
      title: "Nequi",
      description: "Transferencia desde la app Nequi",
      details: [
        "1. Ingresar a Nequi → Envía → Otros Bancos",
        `2. Nombre: ${institutionInfo.name}`,
        `3. Tipo documento: NIT | NIT: ${institutionInfo.nit}`,
        `4. Banco: ${institutionInfo.bank}`,
        `5. Tipo cuenta: Ahorros | Cuenta: ${institutionInfo.account}`,
        "6. Valor: $30.000 (1 estudiante), $40.000 (2), $50.000 (3)"
      ],
    },
    {
      icon: <AttachMoney sx={{ color: theme.palette.success.main }} />,
      title: "SuperGiros",
      description: "Disponible a partir del viernes 28 de noviembre",
      details: [
        "Convenio de recaudo con Banco Mundo Mujer",
        `NIT: ${institutionInfo.nit}`,
        `Cuenta: ${institutionInfo.account}`
      ],
      badge: "Desde Nov 28"
    }
  ];

  return (
    <Box sx={{ maxWidth: 1200, margin: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          Información de Pago
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Institución Educativa José Eusebio Caro
        </Typography>
      </Box>

      <Alert 
        severity="warning" 
        icon={<Download />}
        sx={{ mt: 4, fontSize: '1rem' }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Importante: Descarga e imprime tu comprobante de pago
        </Typography>
        <Typography variant="body2">
          Debes presentar el comprobante impreso para realizar la matrícula
        </Typography>
      </Alert>

      {/* Bank Account Info Card */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          //background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.dark',
          border: `3px solid ${theme.palette.secondary.main}`
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <AccountBalance sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Cuenta de Recaudo Institucional
            </Typography>
          </Box>
          <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.3)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Nombre</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {institutionInfo.name}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2, width: '100%', mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Banco</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Mundo Mujer</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>NIT</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>800046335</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Cuenta de Ahorros</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>370-149678963-01</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Payment Options */}
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
        Opciones de Pago
      </Typography>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
        {paymentOptions.map((option, index) => (
          <Card
            key={index}
            elevation={2}
            sx={{
              position: 'relative',
              border: option.highlight
                ? `2px solid ${theme.palette.secondary.main}`
                : `1px solid #e0e0e0`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
                borderColor: option.highlight ? theme.palette.secondary.main : theme.palette.primary.main
              }
            }}
          >
            {option.badge && (
              <Chip 
                label={option.badge}
                size="small"
                color="error"
                sx={{ position: 'absolute', top: 10, right: 10 }}
              />
            )}
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  mr: 2, 
                  p: 1, 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {option.icon}
                </Box>
                <Box>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <List dense>
                {option.details.map((detail, idx) => (
                  <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={detail}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontSize: '0.9rem' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Important Alert */}

    </Box>
  );
};

export default PaymentInstructions;
